import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { listSuppliers, listUnassignedVariants } from "../api/purchase_orders";
import { t } from "../lib/i18n";
import type { MeResponse } from "../types";
import { GatedQueryError } from "./GatedQueryError";

/** 未割当件数は総数しか使わないので1件だけ引く（一覧は注文書画面が持つ）。 */
const UNASSIGNED_PROBE_LIMIT = 1;

/** 発注点は Starter 以上でしか設定できない（router 全体が Starter+ ゲート）。 */
const REORDER_MIN_PLAN_CODES = ["starter", "pro", "business"];

interface ChecklistItem {
  id: string;
  label: string;
  hint: string | null;
  isDone: boolean;
  actionLabel: string;
  path: string;
}

interface SetupChecklistProps {
  me: MeResponse;
}

/**
 * ホームの「最初にやること」を、実際の設定状況で完了判定するチェックリスト。
 *
 * 背景: 旧実装は「①同期 ②移行 ③通知設定」の静的3項目で、完了状態を持たず、
 * **今回つまずいた仕入先と発注点がそもそも載っていなかった**（2026-07-30 に dev ストアで
 * 実際に手が止まった箇所）。App Store 経由で入る店は Stocky ユーザーとは限らないため、
 * Stocky ユーザーでない店もあるため、移行は必須件数や完了判定には含めない。
 *
 * 完了判定のシグナルが取れなかったときは**チェックリストを出さず `GatedQueryError` に委ねる**
 * （商品数上限の403ならアップグレード導線・一時的な失敗なら再試行）。⚠️ 以前ここは
 * 「失敗＝未完了」に倒していたが、それだと**押しても403の画面に着くだけの行き止まりの
 * ボタン**になる＝空状態に行き先を付ける作業をしながら行き先の無いボタンを作っていた。
 * 分岐は 9 画面で使っている既存の共通処理がそのまま使える（独自に書かない）。
 *
 * 判定そのものは fail-safe 側に倒したままにする（`?? 0`）＝万一シグナルが欠けても
 * 「できていないのに完了」とは表示しない。
 *
 * 🔴 発注点の信号を**発注点エンドポイントから引かない**のは意図的。あの router は
 * 丸ごと Starter+ ゲートで Free には必ず 403 を返すため、ここで拾うと
 * 「新規インストール直後の全店舗（＝Free）がチェックリストの代わりに、押しても
 * 必ず失敗する再試行ボタンを見る」ことになる（実際にそうなっていた）。信号は
 * 非ゲートの `unassigned-variants` に相乗りさせてある。
 */
export function SetupChecklist({ me }: SetupChecklistProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const suppliersQuery = useQuery({ queryKey: ["po-suppliers"], queryFn: listSuppliers });
  const unassignedQuery = useQuery({
    queryKey: ["setup-unassigned-total"],
    queryFn: () => listUnassignedVariants("", UNASSIGNED_PROBE_LIMIT, 0),
  });

  // 初回ロード中は何も出さない（設定済みの店でカードが一瞬閃くのを避ける）。
  if (suppliersQuery.isLoading || unassignedQuery.isLoading) {
    return null;
  }

  if (suppliersQuery.isError || unassignedQuery.isError) {
    return (
      <GatedQueryError
        title={t("セットアップ状況の取得に失敗しました")}
        onRetry={() => {
          void suppliersQuery.refetch();
          void unassignedQuery.refetch();
        }}
      />
    );
  }

  const hasSuppliers = (suppliersQuery.data?.length ?? 0) > 0;
  const unassignedTotal = unassignedQuery.data?.total ?? 0;
  const assignedTotal = unassignedQuery.data?.assigned_total;
  // 「仕入先を登録して割り当てる」の完了条件は**割当が1件以上あること**。
  // 仕入先の有無だけで完了にすると、1件も割り当てていない店の注文書が空のまま＝
  // このチェックリストが塞ぐはずだった閉ループがそのまま残る。
  // 🔴 `assigned_total` が無い応答（この列より古い backend）では従来ルールへ落とす
  //    ＝別々にデプロイされる間、割当済みの店に終わらないチェックリストを見せない。
  const hasAssignedProducts =
    typeof assignedTotal === "number" ? assignedTotal > 0 : hasSuppliers;
  // 発注点のカバレッジ。母集団は割当済み商品＝実際に発注しうるもの。
  const policyCovered = unassignedQuery.data?.policy_covered_total;
  const policyUncovered = unassignedQuery.data?.policy_uncovered_total ?? 0;
  const hasReorderPointCoverage = (policyCovered ?? 0) > 0;
  const canSetReorderPoints =
    me.subscription.plan_code !== null &&
    REORDER_MIN_PLAN_CODES.includes(me.subscription.plan_code);
  const canUseEmailAlerts = canSetReorderPoints;
  const hasNotificationTarget =
    canUseEmailAlerts &&
    (me.alert_email !== null ||
      me.alert_slack_webhook_url !== null ||
      me.line.linked);

  const items: ChecklistItem[] = [
    {
      id: "sync",
      label: t("在庫データを同期する"),
      hint: null,
      isDone: me.last_sync_at !== null,
      actionLabel: t("同期する"),
      path: "/dashboard",
    },
    {
      id: "suppliers",
      label: t("仕入先を登録して商品に割り当てる"),
      // 発注しない商品は割り当てなくてよいので、未割当が残っていても未完了にはしない。
      // ただし件数は伝える（Stocky 移行は supplier_id を書かないので大量に残りうる）。
      hint:
        hasSuppliers && unassignedTotal > 0
          ? t("仕入先が未割当の商品が{count}件あります").replace(
              "{count}",
              String(unassignedTotal),
            )
          : null,
      isDone: hasAssignedProducts,
      actionLabel: t("仕入先を設定する"),
      path: "/purchase-orders",
    },
    {
      id: "notifications",
      label: t("通知の受け取り先を決める"),
      // チャネル名は必ず出す。初回導線に無いチャネルは店から見て存在しないのと同じで、
      // LINE は長らくここから抜けていた（料金表では Pro の特典として売っている）。
      hint: canUseEmailAlerts
        ? t("メール（Starter以上）/ Slack・LINE（Pro以上）から選べます")
        : t("在庫アラートのメール通知はStarter以上で利用できます"),
      isDone: hasNotificationTarget,
      actionLabel: canUseEmailAlerts ? t("通知先を設定する") : t("プランを見る"),
      path: canUseEmailAlerts ? "/settings" : "/plans",
    },
  ];

  // 任意項目＝やらなくても店は回る。必須の残り件数には数えず、完了判定にも
  // 影響させない（数えると「終わらないチェックリスト」に戻る）。
  // 🔴 発注点がここに居るのは、発注点なしでも注文書が使えるようになったため
  //    （backend: policy が無い商品も数量空欄で明細に並ぶ）。発注点は
  //    **自動提案を効かせるための設定**であって発注の前提ではない。
  const optionalItems: ChecklistItem[] = [
    {
      id: "reorder-points",
      label: t("発注点を設定する"),
      hint: canSetReorderPoints
        ? policyUncovered > 0
          ? `${t("設定すると自動提案が効きます")}｜${t("発注点が未設定の商品が{count}件あります").replace("{count}", String(policyUncovered))}`
          : t("設定すると自動提案が効きます")
        : t("設定すると自動提案が効きます（Starter 以上）"),
      isDone: canSetReorderPoints && hasReorderPointCoverage,
      // Free は発注点ルーターが 403＝押しても行き止まりなので料金表へ送る。
      actionLabel: canSetReorderPoints ? t("発注点を開く") : t("プランを見る"),
      path: canSetReorderPoints ? "/reorder-policies" : "/plans",
    },
  ];

  const isAllDone = items.every((item) => item.isDone);
  const remainingCount = items.filter((item) => !item.isDone).length;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm">
              {isAllDone ? t("初期セットアップは完了しています") : t("最初にやること")}
            </Text>
            {!isAllDone && (
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("残り{count}件").replace("{count}", String(remainingCount))}
              </Text>
            )}
          </BlockStack>
          {isAllDone && (
            <Button variant="plain" onClick={() => setIsExpanded((open) => !open)}>
              {isExpanded ? t("閉じる") : t("セットアップの内容を見る")}
            </Button>
          )}
        </InlineStack>

        {(!isAllDone || isExpanded) && (
          <BlockStack gap="300">
            {[...items, ...optionalItems].map((item) => (
              <InlineStack key={item.id} align="space-between" blockAlign="center" gap="200">
                <BlockStack gap="050">
                  <Text as="span" variant="bodyMd">
                    {item.label}
                  </Text>
                  {item.hint && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      {item.hint}
                    </Text>
                  )}
                </BlockStack>
                {item.isDone ? (
                  <Badge tone="success">{t("完了")}</Badge>
                ) : (
                  <Button onClick={() => navigate(item.path)}>{item.actionLabel}</Button>
                )}
              </InlineStack>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
